/**
 * The bell in the header.
 *
 * The panel arrives rendered from the server, this module only keeps it in
 * sync: it marks an entry as read the moment it is opened, empties the whole
 * list on demand and refreshes the badge in the background.
 */

import { relativeDate } from "../core/dates";
import { text } from "../core/i18n";
import { query, queryAll } from "../core/dom";
import { getJson, postJson } from "../core/http";
import { toast } from "../core/toast";
import type { NotificationListResponse, NotificationResponse, NotificationStateResponse } from "../core/types";

const UNREAD_CLASS = "is-unread";
const EMPTY_CLASS = "is-empty";
const ERROR_KEY = "js.notifications.error";
const ERROR_FALLBACK = "Oznámenia sa nepodarilo načítať.";

/** How often the badge asks the server whether something new arrived. */
export const REFRESH_INTERVAL = 60_000;

/** How many entries the dropdown shows. */
export const PANEL_LIMIT = 6;

const LIST_URL = "/api/notifications";
const READ_ALL_URL = "/api/notifications/read";

/** Return the endpoint marking the notification `id` as read. */
export function readUrl(id: number): string {
    return `${LIST_URL}/${id}/read`;
}

/**
 * Return a relative description of how old `stamp` is.
 *
 * The rows of the bell are re-rendered here after every poll, so they are
 * dated exactly like the ones the server wrote into the page.
 */
export function relativeMoment(stamp: string, now: Date = new Date()): string {
    const moment = new Date(stamp);
    if (Number.isNaN(moment.getTime())) {
        return "";
    }

    return relativeDate(moment.toISOString(), now);
}

/** Return the text of the badge for `unread`. */
export function badgeText(unread: number): string {
    if (unread <= 0) {
        return "0";
    }

    return unread < 100 ? String(unread) : "99+";
}

/** Write `unread` into every badge and counter of the header. */
export function renderBadge(root: HTMLElement, unread: number): void {
    const badge = query<HTMLElement>("[data-notification-badge]", root);
    if (badge) {
        badge.textContent = badgeText(unread);
        badge.classList.toggle(EMPTY_CLASS, unread <= 0);
    }

    const counter = query<HTMLElement>("[data-notification-count]", root);
    if (counter) {
        counter.textContent =
            unread > 0
                ? text("js.notifications.unread", "{count} neprečítaných", { count: unread })
                : text("js.notifications.all_read", "Všetko prečítané");
    }

    const toggle = query<HTMLElement>("[data-notification-toggle]", root);
    toggle?.setAttribute(
        "aria-label",
        unread > 0
            ? text("js.notifications.aria_unread", "Oznámenia, neprečítaných: {count}", { count: unread })
            : text("js.notifications.aria", "Oznámenia"),
    );
}

/** Build the markup of a single row, mirroring `partials/notifications.html`. */
export function renderItem(item: NotificationResponse): HTMLElement {
    const link = document.createElement("a");
    link.className = item.is_read ? "notification-item" : `notification-item ${UNREAD_CLASS}`;
    link.href = item.url ?? "/notifications";
    link.dataset["notificationId"] = String(item.id);

    const icon = document.createElement("span");
    icon.className = "notification-item__icon";
    icon.innerHTML =
        `<svg class="icon" width="15" height="15" aria-hidden="true">` +
        `<use href="/static/assets/icons/icons.svg#${item.icon}"></use></svg>`;

    const main = document.createElement("span");
    main.className = "notification-item__main";

    const title = document.createElement("span");
    title.className = "notification-item__title";
    title.textContent = item.title;
    main.appendChild(title);

    if (item.body) {
        const body = document.createElement("span");
        body.className = "notification-item__body";
        body.textContent = item.body;
        main.appendChild(body);
    }

    const meta = document.createElement("span");
    meta.className = "notification-item__meta";
    meta.textContent = `${item.kind_label} • ${relativeMoment(item.created_at)}`;
    main.appendChild(meta);

    link.append(icon, main);

    return link;
}

/** Replace the rows of the dropdown with `items`. */
export function renderList(root: HTMLElement, items: readonly NotificationResponse[]): void {
    const list = query<HTMLElement>("[data-notification-list]", root);
    if (!list) {
        return;
    }

    list.innerHTML = "";

    if (items.length === 0) {
        const empty = document.createElement("p");
        empty.className = "notification-panel__empty";
        empty.textContent = text("js.notifications.empty", "Zatiaľ nemáte žiadne oznámenia.");
        list.appendChild(empty);

        return;
    }

    for (const item of items) {
        list.appendChild(renderItem(item));
    }
}

/** Mark every row of `root` as read in the interface. */
function clearUnreadRows(root: HTMLElement): void {
    for (const row of queryAll<HTMLElement>(".notification-item", root)) {
        row.classList.remove(UNREAD_CLASS);
    }
}

/** Wire the single notification menu of the page. */
function initNotificationMenu(root: HTMLElement): void {
    let timer: ReturnType<typeof setInterval> | undefined;

    const refresh = (): void => {
        void getJson<NotificationListResponse>(`${LIST_URL}?limit=${PANEL_LIMIT}`)
            .then((payload) => {
                renderBadge(root, payload.unread);
                renderList(root, payload.items);
            })
            .catch(() => {
                // A refresh in the background must never interrupt the visitor,
                // the panel simply keeps whatever the server rendered.
            });
    };

    root.addEventListener("click", (event: Event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) {
            return;
        }

        const readAll = target.closest<HTMLElement>("[data-notification-read-all]");
        if (readAll) {
            event.preventDefault();
            clearUnreadRows(root);
            renderBadge(root, 0);

            void postJson<NotificationStateResponse>(READ_ALL_URL, {})
                .then((state) => renderBadge(root, state.unread))
                .catch(() => {
                    toast(text(ERROR_KEY, ERROR_FALLBACK), "error");
                    refresh();
                });

            return;
        }

        // Opening a notification marks it as read, the browser follows the
        // link right after, so the request is fired without waiting for it.
        const item = target.closest<HTMLElement>("[data-notification-id]");
        if (!item) {
            return;
        }

        const id = Number.parseInt(item.dataset["notificationId"] ?? "", 10);
        if (!Number.isFinite(id) || !item.classList.contains(UNREAD_CLASS)) {
            return;
        }

        item.classList.remove(UNREAD_CLASS);
        void postJson<NotificationStateResponse>(readUrl(id), {})
            .then((state) => renderBadge(root, state.unread))
            .catch(() => undefined);
    });

    const tick = (): void => {
        if (document.visibilityState === "visible") {
            refresh();
        }
    };

    timer = setInterval(tick, REFRESH_INTERVAL);
    document.addEventListener("visibilitychange", tick);
    window.addEventListener("pagehide", () => {
        if (timer !== undefined) {
            clearInterval(timer);
            timer = undefined;
        }
    });
}

/** Wire the notification menu when the page has one. */
export function initNotifications(): void {
    const root = query<HTMLElement>("[data-notification-menu]");
    if (root) {
        initNotificationMenu(root);
    }
}
